import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { getProsConsoleEarnings, type ProsConsoleEarning, type ProsConsoleEarningsResponse } from '../../api/prosConsole'

const TYPE_OPTIONS = [
  { value: 'earnings', label: 'Earnings' },
  { value: 'all', label: 'All types' },
  { value: 'escrow_funded', label: 'Escrow funded (held)' },
  { value: 'escrow_released', label: 'Escrow released (earned)' },
  { value: 'fee', label: 'Platform fee' },
  { value: 'refund', label: 'Refund' },
  { value: 'dispute_split', label: 'Dispute split' },
  { value: 'stake', label: 'Stake (program)' },
] as const

const TYPE_LABELS: Record<string, string> = {
  escrow_funded: 'Escrow funded (held)',
  escrow_released: 'Escrow released (earned)',
  fee: 'Platform fee',
  refund: 'Refund',
  dispute_split: 'Dispute split',
  stake: 'Stake (program)',
}

function formatPixe(value?: number | null): string {
  if (value == null || Number.isNaN(Number(value))) return '—'
  return `${Math.trunc(Number(value)).toLocaleString()} PIXE`
}

function amountClass(value?: number | null): string {
  if ((value || 0) < 0) return 'text-amber-200'
  return 'text-emerald-200'
}

export default function ProsEarningsTab() {
  const [typeFilter, setTypeFilter] = useState<string>('earnings')
  const [rows, setRows] = useState<ProsConsoleEarning[]>([])
  const [kpis, setKpis] = useState<ProsConsoleEarningsResponse['kpis'] | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    getProsConsoleEarnings({ page: 1, page_size: 50, type: typeFilter })
      .then((payload) => {
        if (cancelled) return
        setRows(Array.isArray(payload?.results) ? payload.results : [])
        setKpis(payload?.kpis || null)
      })
      .catch((e: any) => {
        if (!cancelled) setError(e?.response?.data?.detail || e?.message || 'Failed to load earnings.')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [typeFilter])

  const cards = useMemo(
    () => [
      { label: 'Earned (released, 30d)', value: kpis?.earned_released_30d_pixe },
      { label: 'Earned (lifetime)', value: kpis?.earned_released_lifetime_pixe },
      { label: 'Held in escrow (current)', value: kpis?.held_pixe },
      { label: 'Fees paid (30d)', value: kpis?.fees_30d_pixe },
    ],
    [kpis],
  )

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
        {cards.map((card) => (
          <div key={card.label} className="rounded-xl border border-white/10 bg-white/5 p-4">
            <div className="text-xs text-white/60">{card.label}</div>
            <div className="mt-1 text-xl font-bold text-white">{formatPixe(card.value)}</div>
          </div>
        ))}
      </div>

      <div className="rounded-xl border border-white/10 bg-white/5 p-4">
        <div className="mb-3 flex items-center justify-between gap-2">
          <div className="text-sm font-semibold text-white">Earnings activity</div>
          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="rounded-lg border border-white/15 bg-black/30 px-3 py-2 text-xs text-white">
            {TYPE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        {loading ? <div className="text-sm text-white/70">Loading earnings activity...</div> : null}
        {error ? <div className="text-sm text-red-300">{error}</div> : null}
        {!loading && !error && rows.length === 0 ? (
          <div className="text-sm text-white/70">No earnings yet. Once clients approve delivered work, escrow releases here.</div>
        ) : null}
        <div className="space-y-2">
          {rows.map((row, idx) => (
            <div key={`${row.type}-${row.at}-${idx}`} className="flex items-center justify-between gap-2 rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-sm">
              <div>
                <div className="text-white/90">{row.title || TYPE_LABELS[row.type] || row.type.replaceAll('_', ' ')}</div>
                <div className="text-xs text-white/60">{row.at ? new Date(row.at).toLocaleString() : '-'}</div>
              </div>
              <div className="flex items-center gap-2">
                <div className={amountClass(row.amount_pixe)}>{formatPixe(row.amount_pixe || 0)}</div>
                {row.job_id ? <Link to={`/jobs/${row.job_id}`} className="rounded border border-white/15 bg-white/5 px-2 py-1 text-xs hover:bg-white/10">Job</Link> : null}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
